import React, { useState } from 'react';
import { 
    Stack, 
    Typography, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    TextField, 
    IconButton, 
    Button,
    Card,
    CardContent,
    CardActions,
    CardMedia,
    Chip,
    Box,
    Fab,
    Skeleton,
    Zoom,
    Fade,
    Avatar,
    LinearProgress,
    Alert
} from '@mui/material';
import { 
    Close, 
    Edit, 
    CloudUpload, 
    PictureAsPdf, 
    Image as ImageIcon,
    InsertDriveFile,
    Visibility,
    Delete,
    Save,
    CheckCircle
} from '@mui/icons-material';
import './styles/Template_Card.css';
import instance from '../Utils/api';
import { toast } from 'react-toastify';

const Template_Card = ({ data, setUpdate }) => {
    const [openModal, setOpenModal] = useState(false);
    const [description, setDescription] = useState(data.description);
    const [image, setImage] = useState(null);
    const [imagedata, setImagedata] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const [hovered, setHovered] = useState(false);

    // Custom color palette based on #621D32
    const colors = {
        primary: '#621D32',        // Main burgundy
        primaryLight: '#8B2C47',   // Lighter burgundy
        primaryDark: '#4A1626',    // Darker burgundy
        secondary: '#D4AF37',      // Gold accent
        tertiary: '#2C5F2D',       // Forest green
        accent: '#E8C5A0',         // Cream/beige
        surface: '#FDF8F5',        // Light cream background
        surfaceDark: '#F5E6D8',    // Darker cream
        text: '#2C1810',           // Dark brown text
        textSecondary: '#5D4E37',  // Medium brown text
    };

    // File type detection functions
    const getFileExtension = (url) => {
        if (!url) return '';
        const parts = url.split('.');
        return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
    };

    const getFileType = (url) => {
        const extension = getFileExtension(url);
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        const pdfExtensions = ['pdf'];
        
        if (imageExtensions.includes(extension)) {
            return 'image';
        } else if (pdfExtensions.includes(extension)) {
            return 'pdf';
        } else {
            return 'unknown';
        }
    };

    const fileUrl = `http://localhost:4050/public/${data.image}`;
    const fileType = getFileType(fileUrl);
    const fileExtension = getFileExtension(fileUrl);

    const handleEdit = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setImage(null);
        setImagedata(null);
        setDescription(data.description);
    };

    const handleDescriptionChange = (event) => {
        setDescription(event.target.value);
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('File size must be less than 5MB');
                return;
            }
            
            setImagedata(file);
            setImage(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const formdata = new FormData();
            formdata.append('id', data?._id);
            if (imagedata) {
                formdata.append('image', imagedata);
            }
            formdata.append('description', description);
            
            const response = await instance.put('/editProduct', formdata);
            if (response.data.success === true) {
                toast.success(response.data.message);
                setOpenModal(false);
                setUpdate();
                setImage(null);
                setImagedata(null);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to update product');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setOpenModal(false);
        setDescription(data.description);
        setImage(null);
        setImagedata(null);
    };

    const getFileIcon = (type) => {
        switch (type) {
            case 'image':
                return <ImageIcon />;
            case 'pdf':
                return <PictureAsPdf />;
            default:
                return <InsertDriveFile />;
        }
    };

    const getFileColor = (type) => {
        switch (type) {
            case 'image':
                return colors.tertiary;
            case 'pdf':
                return colors.primary;
            default:
                return colors.textSecondary;
        }
    };

    const getFileGradient = (type) => {
        switch (type) {
            case 'image':
                return `linear-gradient(135deg, ${colors.tertiary} 0%, ${colors.primaryLight} 100%)`;
            case 'pdf':
                return `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`;
            default:
                return `linear-gradient(135deg, ${colors.textSecondary} 0%, ${colors.primary} 100%)`;
        }
    };

    const handleImageLoad = () => {
        setImageLoading(false);
    };

    return (
        <>
            <Zoom in timeout={300}>
                <Card 
                    sx={{ 
                        maxWidth: 380, 
                        margin: 2, 
                        position: 'relative',
                        borderRadius: 3,
                        overflow: 'hidden',
                        background: `linear-gradient(145deg, ${colors.surface} 0%, ${colors.surfaceDark} 100%)`,
                        border: `1px solid ${colors.accent}`,
                        transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                        cursor: 'pointer',
                        '&:hover': {
                            transform: 'translateY(-8px) scale(1.02)',
                            boxShadow: `0 20px 40px ${colors.primary}25`,
                            borderColor: colors.primaryLight,
                        },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: getFileGradient(fileType),
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            zIndex: 0,
                        },
                        '&:hover::before': {
                            opacity: 0.05,
                        }
                    }}
                    elevation={4}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                    {/* Animated File Type Badge */}
                    <Zoom in timeout={500}>
                        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
                            <Chip
                                icon={getFileIcon(fileType)}
                                label={fileExtension.toUpperCase()}
                                size="small"
                                sx={{ 
                                    background: `linear-gradient(135deg, ${colors.surface} 0%, ${colors.accent} 100%)`,
                                    color: getFileColor(fileType),
                                    backdropFilter: 'blur(10px)',
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    border: `2px solid ${getFileColor(fileType)}`,
                                    boxShadow: `0 4px 12px ${getFileColor(fileType)}25`,
                                    transition: 'all 0.3s ease',
                                    '& .MuiChip-icon': {
                                        color: getFileColor(fileType),
                                    },
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                        boxShadow: `0 6px 16px ${getFileColor(fileType)}35`,
                                    }
                                }}
                            />
                        </Box>
                    </Zoom>

                    {/* File Display with Enhanced Design */}
                    {fileType === 'image' && (
                        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                            {imageLoading && (
                                <Skeleton 
                                    variant="rectangular" 
                                    height={240} 
                                    animation="wave"
                                    sx={{ bgcolor: colors.accent }}
                                />
                            )}
                            <CardMedia
                                component="img"
                                height="240"
                                image={fileUrl}
                                alt={`Product ${data._id}`}
                                onLoad={handleImageLoad}
                                sx={{
                                    objectFit: 'cover',
                                    backgroundColor: colors.surfaceDark,
                                    transition: 'transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                    '&:hover': {
                                        transform: 'scale(1.08)',
                                    },
                                    display: imageLoading ? 'none' : 'block'
                                }}
                            />
                            {/* Overlay for better text visibility */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: '50%',
                                    background: `linear-gradient(transparent, ${colors.primary}30)`,
                                    opacity: hovered ? 1 : 0,
                                    transition: 'opacity 0.3s ease'
                                }}
                            />
                        </Box>
                    )}

                    {fileType === 'pdf' && (
                        <Box 
                            sx={{ 
                                height: 240, 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center', 
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                                color: colors.surface,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '-50%',
                                    left: '-50%',
                                    width: '200%',
                                    height: '200%',
                                    background: `radial-gradient(circle, ${colors.secondary}20 0%, transparent 70%)`,
                                    animation: 'rotate 20s linear infinite',
                                },
                                '&:hover': {
                                    '&::before': {
                                        animationDuration: '10s',
                                    }
                                }
                            }}
                            onClick={() => window.open(fileUrl, '_blank')}
                        >
                            <Avatar 
                                sx={{ 
                                    bgcolor: `${colors.surface}20`, 
                                    width: 80, 
                                    height: 80, 
                                    mb: 2,
                                    backdropFilter: 'blur(10px)',
                                    border: `2px solid ${colors.secondary}`,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                        bgcolor: `${colors.secondary}30`,
                                        borderColor: colors.accent,
                                    }
                                }}
                            >
                                <PictureAsPdf sx={{ fontSize: 40, color: colors.surface }} />
                            </Avatar>
                            <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: colors.surface }}>
                                PDF Document
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, color: colors.accent }}>
                                Click to view
                            </Typography>
                        </Box>
                    )}

                    {fileType === 'unknown' && (
                        <Box 
                            sx={{ 
                                height: 240, 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center', 
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${colors.textSecondary} 0%, ${colors.primary} 100%)`,
                                color: colors.surface
                            }}
                        >
                            <Avatar sx={{ 
                                bgcolor: `${colors.surface}20`, 
                                width: 64, 
                                height: 64, 
                                mb: 2,
                                border: `2px solid ${colors.accent}`,
                            }}>
                                <InsertDriveFile sx={{ fontSize: 32, color: colors.surface }} />
                            </Avatar>
                            <Typography variant="body1" fontWeight="600" gutterBottom sx={{ color: colors.surface }}>
                                Unsupported File
                            </Typography>
                            <Button 
                                size="small" 
                                variant="contained"
                                onClick={() => window.open(fileUrl, '_blank')}
                                sx={{ 
                                    mt: 1,
                                    bgcolor: `${colors.surface}20`,
                                    color: colors.surface,
                                    border: `1px solid ${colors.accent}`,
                                    '&:hover': {
                                        bgcolor: `${colors.accent}30`,
                                        borderColor: colors.secondary,
                                    }
                                }}
                            >
                                Open File
                            </Button>
                        </Box>
                    )}

                    <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography 
                            variant="body1" 
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minHeight: '72px',
                                lineHeight: 1.6,
                                fontWeight: 400,
                                fontSize: '0.95rem',
                                color: colors.text,
                            }}
                        >
                            {data.description}
                        </Typography>
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                        <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => window.open(fileUrl, '_blank')}
                            sx={{ 
                                color: colors.textSecondary,
                                '&:hover': {
                                    bgcolor: `${colors.primary}10`,
                                    color: colors.primary,
                                }
                            }}
                        >
                            View
                        </Button>
                        
                        <Fab
                            size="small"
                            onClick={handleEdit}
                            sx={{
                                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                                color: colors.surface,
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                    boxShadow: `0 8px 25px ${colors.primary}40`,
                                    background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.secondary} 100%)`,
                                }
                            }}
                        >
                            <Edit />
                        </Fab>
                    </CardActions>
                </Card>
            </Zoom>

            {/* Enhanced Edit Modal */}
            <Dialog 
                open={openModal} 
                onClose={handleCloseModal} 
                maxWidth="md" 
                fullWidth
                TransitionComponent={Fade}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: `linear-gradient(145deg, ${colors.surface} 0%, ${colors.surfaceDark} 100%)`,
                        minHeight: '600px',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle 
                    sx={{ 
                        pb: 2,
                        borderBottom: `2px solid ${colors.accent}`,
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                        color: colors.surface,
                        position: 'relative'
                    }}
                >
                    <Typography variant="h5" component="div" fontWeight="600">
                        Edit Product
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, color: colors.accent }}>
                        Update your product information and files
                    </Typography>
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            right: 12,
                            top: 12,
                            color: colors.surface,
                            bgcolor: `${colors.surface}10`,
                            border: `1px solid ${colors.accent}`,
                            '&:hover': {
                                bgcolor: `${colors.secondary}20`,
                                borderColor: colors.secondary,
                            }
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    {loading && (
                        <LinearProgress 
                            sx={{ 
                                mb: 2,
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: colors.primary,
                                }
                            }} 
                        />
                    )}
                    
                    <Stack spacing={4}>
                        {/* Current File Display */}
                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="600" sx={{ color: colors.text }}>
                                Current File
                            </Typography>
                            <Card 
                                variant="outlined" 
                                sx={{ 
                                    p: 3, 
                                    background: `linear-gradient(145deg, ${colors.surfaceDark} 0%, ${colors.accent} 100%)`,
                                    border: `2px solid ${colors.accent}`,
                                    borderRadius: 2
                                }}
                            >
                                <Stack direction="row" spacing={3} alignItems="center">
                                    <Avatar 
                                        sx={{ 
                                            bgcolor: getFileColor(fileType),
                                            width: 56,
                                            height: 56,
                                            border: `2px solid ${colors.surface}`,
                                        }}
                                    >
                                        {getFileIcon(fileType)}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: colors.text }}>
                                            {fileExtension.toUpperCase()} File
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                            {data.image}
                                        </Typography>
                                    </Box>
                                    <Button 
                                        variant="contained"
                                        startIcon={<Visibility />}
                                        onClick={() => window.open(fileUrl, '_blank')}
                                        sx={{
                                            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                                            color: colors.surface,
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 8px 25px ${colors.primary}35`,
                                                background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.secondary} 100%)`,
                                            }
                                        }}
                                    >
                                        View File
                                    </Button>
                                </Stack>
                            </Card>
                        </Box>

                        {/* File Upload Section */}
                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="600" sx={{ color: colors.text }}>
                                Upload New File
                            </Typography>
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                onChange={handleImageChange}
                                id="file-upload"
                                style={{ display: 'none' }}
                            />
                            <label htmlFor="file-upload">
                                <Card
                                    component="div"
                                    variant="outlined"
                                    sx={{ 
                                        p: 4,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        border: `3px dashed ${colors.primary}`,
                                        borderRadius: 2,
                                        background: `linear-gradient(145deg, ${colors.surface} 0%, ${colors.surfaceDark} 100%)`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: colors.secondary,
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 8px 25px ${colors.primary}15`,
                                            background: `linear-gradient(145deg, ${colors.surfaceDark} 0%, ${colors.accent} 100%)`,
                                        }
                                    }}
                                >
                                    <CloudUpload sx={{ fontSize: 48, color: colors.primary, mb: 2 }} />
                                    <Typography variant="h6" gutterBottom sx={{ color: colors.text }}>
                                        Choose File
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                        Support for Images & PDF files (Max 5MB)
                                    </Typography>
                                </Card>
                            </label>

                            {/* Preview for uploaded file */}
                            {image && (
                                <Fade in timeout={500}>
                                    <Card variant="outlined" sx={{ mt: 3, overflow: 'hidden', borderRadius: 2, border: `2px solid ${colors.tertiary}` }}>
                                        <Box sx={{ p: 2, background: `linear-gradient(135deg, ${colors.tertiary} 0%, ${colors.primaryLight} 100%)` }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <CheckCircle sx={{ color: colors.surface }} />
                                                <Typography variant="subtitle1" sx={{ color: colors.surface }} fontWeight="600">
                                                    New File Selected
                                                </Typography>
                                            </Stack>
                                        </Box>
                                        <CardContent sx={{ bgcolor: colors.surface }}>
                                            <Box 
                                                sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'center',
                                                    mt: 1
                                                }}
                                            >
                                                <img
                                                    src={image}
                                                    alt="Preview"
                                                    style={{
                                                        maxWidth: '250px',
                                                        maxHeight: '250px',
                                                        objectFit: 'contain',
                                                        borderRadius: '8px',
                                                        boxShadow: `0 4px 12px ${colors.primary}25`
                                                    }}
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Fade>
                            )}
                        </Box>

                        {/* Description Field */}
                        {/* <Box>
                            <Typography variant="h6" gutterBottom fontWeight="600" sx={{ color: colors.text }}>
                                Description
                            </Typography>
                            <TextField
                                multiline
                                rows={4}
                                value={description}
                                onChange={handleDescriptionChange}
                                fullWidth
                                variant="outlined"
                                placeholder="Enter product description..."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: colors.surface,
                                        '& fieldset': {
                                            borderColor: colors.accent,
                                            borderWidth: '2px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: colors.primary,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: colors.primary,
                                            borderWidth: '2px',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: colors.text,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: colors.textSecondary,
                                    },
                                }}
                            />
                        </Box> */}
                    </Stack>
                </DialogContent>

                <DialogActions 
                    sx={{ 
                        px: 3, 
                        py: 3, 
                        borderTop: `2px solid ${colors.accent}`,
                        gap: 2,
                        bgcolor: colors.surfaceDark
                    }}
                >
                    <Button 
                        onClick={handleCancel} 
                        variant="outlined"
                        size="large"
                        startIcon={<Close />}
                        disabled={loading}
                        sx={{
                            minWidth: 120,
                            borderRadius: 2,
                            borderColor: colors.textSecondary,
                            color: colors.textSecondary,
                            '&:hover': {
                                borderColor: colors.primary,
                                color: colors.primary,
                                bgcolor: `${colors.primary}10`,
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        size="large"
                        startIcon={loading ? null : <Save />}
                        disabled={loading}
                        sx={{
                            minWidth: 140,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                            color: colors.surface,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 25px ${colors.primary}35`,
                                background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.secondary} 100%)`,
                            },
                            '&:disabled': {
                                background: colors.textSecondary,
                                color: colors.accent,
                            }
                        }}
                    >
                        {loading ? 'Updating...' : 'Update Product'}
                    </Button>
                </DialogActions>
            </Dialog>

            <style jsx>{`
                @keyframes rotate {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </>
    );
};

export default Template_Card;
